import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { L } from "./education/DayJourney";
import { WhatsAppReminder } from "./WhatsAppReminder";
import { Users, AlertTriangle, CheckCircle2 } from "lucide-react";

export function MemberProgressPanel() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [coach, setCoach] = useState("ALL");
  const [behindOnly, setBehindOnly] = useState(false);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/coach/member-progress?coach_id=${coach}`);
      setData(data.data);
    } catch (e) { toast.error(extractError(e).message); }
  }, [coach]);

  const loadTemplates = useCallback(async () => {
    try { const { data } = await api.get("/crm/templates"); setTemplates(data.data); } catch { setTemplates([]); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data?.scope === "COACH") loadTemplates(); }, [data?.scope, loadTemplates]);

  const updateItem = (uid, patch) =>
    setData((d) => (d ? { ...d, items: d.items.map((it) => (it.user_id === uid ? { ...it, ...patch } : it)) } : d));

  if (!data) return <p className="text-slate-400">{t("common.loading")}</p>;

  const items = data.items.filter((m) => !behindOnly || m.progress?.is_behind);

  return (
    <div data-testid="member-progress-panel" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
            <Users className="h-4 w-4 text-[#10B981]" /> {t("progress.title")}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            <span data-testid="progress-total-members">{data.total_members}</span> {t("progress.members")} ·{" "}
            <span data-testid="progress-behind-members" className={data.behind_members ? "text-amber-300" : ""}>{data.behind_members}</span> {t("progress.behind")}
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="progress-filter-toggle-wrap">
          <Switch data-testid="progress-behind-toggle" checked={behindOnly} onCheckedChange={setBehindOnly} />
          <Label className="text-slate-300 text-xs cursor-pointer" onClick={() => setBehindOnly((v) => !v)}>{t("crm.behindOnly")}</Label>
        </div>
        {data.scope === "ADMIN" && (
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Coach</Label>
            <Select value={coach} onValueChange={setCoach}>
              <SelectTrigger data-testid="progress-coach-filter" className="w-[220px] bg-[#1C202E] border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#161923] border-white/10 text-slate-200">
                <SelectItem value="ALL">{t("progress.allCoaches")}</SelectItem>
                <SelectItem value="UNASSIGNED">{t("progress.unassigned")}</SelectItem>
                {data.coaches.map((c) => (
                  <SelectItem key={c.user_id} value={c.user_id}>{c.full_name || c.user_id.slice(0, 8)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {data.items.length === 0 ? (
        <p data-testid="progress-empty" className="text-sm text-slate-500">{t("progress.empty")}</p>
      ) : (
        <div className="space-y-2">
          {items.map((m) => {
            const p = m.progress;
            return (
              <Card key={m.enrollment_id} data-testid={`progress-row-${m.enrollment_id}`} className="bg-[#161923] border-white/5">
                <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white truncate">{m.full_name || m.user_id.slice(0, 8)}</span>
                      <span className="font-mono text-[11px] text-slate-500">{m.participant_code}</span>
                      {p?.is_behind ? (
                        <span data-testid={`progress-behind-${m.enrollment_id}`} className="flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                          <AlertTriangle className="h-3 w-3" /> {t("progress.behindBadge")} {p.behind_days}
                        </span>
                      ) : p?.completed_days === p?.total_days ? (
                        <span className="flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">
                          <CheckCircle2 className="h-3 w-3" /> {t("edu.state_COMPLETED")}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-1">
                      <span>{m.batch_name}</span>
                      {m.coach_name && data.scope === "ADMIN" && <span>Coach: {m.coach_name}</span>}
                      {p?.last_completed_at && <span>{t("progress.lastDone")}: {new Date(p.last_completed_at).toLocaleDateString()}</span>}
                    </div>
                  </div>

                  {p ? (
                    <div className="lg:w-72 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span data-testid={`progress-day-${m.enrollment_id}`} className="text-slate-300">
                          {t("edu.day")} {p.current_day} {t("edu.of")} {p.total_days}
                        </span>
                        <span className="font-[Outfit] font-extrabold text-white">{p.percent}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div className={`h-full transition-all ${p.is_behind ? "bg-[#F59E0B]" : "bg-[#10B981]"}`} style={{ width: `${p.percent}%` }} />
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-600">
                        {L(p.journey_name, lang)} · {t("progress.expected")} {p.expected_day}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">{t("progress.noJourney")}</span>
                  )}

                  {data.scope === "COACH" && p?.is_behind && (
                    <div className="lg:w-56 lg:border-l lg:border-white/5 lg:pl-4">
                      <WhatsAppReminder testId={`progress-wa-${m.enrollment_id}`} context="EDUCATION"
                        memberName={m.full_name} whatsappNumber={m.whatsapp_number} memberUserId={m.user_id}
                        lastRemindedAt={m.last_reminded_at} coachNote={m.coach_note}
                        templates={templates} onTemplatesChanged={loadTemplates}
                        onLogged={(ts) => updateItem(m.user_id, { last_reminded_at: ts })}
                        onNoteSaved={(n) => updateItem(m.user_id, { coach_note: n })} />
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
